/**
 * @author Nihar <nihar.baishyar@314ecorp.com>
 * @description utils for payer
 */

import _ from 'lodash';
import { TRANSACTION_TYPE_CODES } from 'app-constants/admin/payer';
import { schemaKeys } from 'components/forms/schema/eligibilityRequestFormSchema';

const DEFAULT_PROVIDER_IDENTIFIER_COMBINATION = [[schemaKeys.npi]];

const NOT_ADDED_PAYER_FILTER = {
	value: 'notAdded',
	label: 'Not Added',
	color: '#262626',
};

const defaultTransactionMeta: Payers.ITransactionMeta = {
	isEligibility: false,
	isClaimStatus: false,
	isEnrollmentRequiredForEligibility: false,
	isEnrollmentRequiredForClaimStatus: false,
};

const getTransactionMeta = (transaction: Payers.ITransaction): Payers.ITransactionMeta => {
	const transactionMeta = _.cloneDeep(defaultTransactionMeta);
	_.map(transaction, ({ transactionType, enrollmentRequired }) => {
		if (transactionType === TRANSACTION_TYPE_CODES.eligibility) {
			transactionMeta.isEligibility = true;
			transactionMeta.isEnrollmentRequiredForEligibility = enrollmentRequired || false;
		} else if (transactionType === TRANSACTION_TYPE_CODES.claimStatus) {
			transactionMeta.isClaimStatus = true;
			transactionMeta.isEnrollmentRequiredForClaimStatus = enrollmentRequired || false;
		}
	});
	return transactionMeta;
};

const prepareSelectedPayersMeta = (data: Payers.ISelectedPayerDetails[]): Payers.ISelectedPayer[] => {
	return _.map(data, (selectedPayer) => {
		const transactionMeta = getTransactionMeta(selectedPayer.transactions);
		return { ...selectedPayer, ...transactionMeta };
	});
};

const prepareAllPayersMeta = (data: Payers.IRegisteredPayerDetails[]): Payers.IRegisteredPayer[] => {
	return _.map(data, (registeredPayer) => {
		const transactionMeta = getTransactionMeta(registeredPayer.transactions);
		return { ...registeredPayer, ...transactionMeta };
	});
};

const preparePayerMeta = (data: Payers.IPayerDetails): Payers.IPayer => {
	const transactionMeta = getTransactionMeta(data.transactions);

	const eligibilityMeta = _.get(data.transactions, TRANSACTION_TYPE_CODES.eligibility) as Payers.IEligibilityMeta;
	if (!_.isEmpty(eligibilityMeta)) {
		eligibilityMeta.requiredFieldCombinations = {
			patient: eligibilityMeta?.requiredFields?.patient || [],
			provider: eligibilityMeta?.supportedProviderIdentifiers || DEFAULT_PROVIDER_IDENTIFIER_COMBINATION,
			encounter: eligibilityMeta?.requiredFields?.encounter || [],
		};
	}
	const claimStatusMeta = _.get(data.transactions, TRANSACTION_TYPE_CODES.claimStatus) as Payers.IClaimStatusMeta;
	if (!_.isEmpty(claimStatusMeta)) {
		claimStatusMeta.requiredFieldCombinations = {
			patient: claimStatusMeta?.requiredFields?.patient || [],
			provider: claimStatusMeta?.supportedProviderIdentifiers || DEFAULT_PROVIDER_IDENTIFIER_COMBINATION,
			Claim: claimStatusMeta?.requiredFields?.Claim || [],
			encounter: claimStatusMeta?.requiredFields?.encounter || [],
		};
	}
	const memberIdMeta = _.get(data.transactions, TRANSACTION_TYPE_CODES.memberId) as Payers.IMemberIdMeta;
	if (!_.isEmpty(memberIdMeta)) {
		memberIdMeta.requiredFieldCombinations = memberIdMeta.requiredFields?.patient || [];
	}
	return {
		...data,
		...transactionMeta,
		eligibilityMeta,
		claimStatusMeta,
		memberIdMeta,
	};
};

/**
 * Groups maintenance data by transaction type, combining markdown content
 */
const groupTransactionMaintenance = (
	maintenanceData: Payers.IPayerMaintenance[],
): Record<Payers.ITransactionType, Payers.IMaintenanceInfo> => {
	const transactionInfo = {} as Record<Payers.ITransactionType, Payers.IMaintenanceInfo>;

	_.forEach(maintenanceData, ({ transactions, timings, isDown = false }) => {
		const markdown = timings?.markdown;
		if (markdown) {
			_.forEach(transactions, (transactionId) => {
				const existingMeta = _.get(transactionInfo, [transactionId]) || ({} as Payers.IMaintenanceInfo);
				if (!existingMeta) {
					transactionInfo[transactionId] = { markdown, isDown };
				}
				//
				else {
					transactionInfo[transactionId] = {
						markdown: existingMeta.markdown ? existingMeta.markdown + markdown : markdown,
						isDown: existingMeta.isDown || isDown,
					};
				}
			});
		}
	});

	return transactionInfo;
};

const getFilteredPayers = (
	registeredPayers: Payers.IRegisteredPayer[],
	selectedPayers: Payers.ISelectedPayer[],
	appliedParams: Payers.IAppliedParams,
): Payers.IRegisteredPayer[] => {
	let filteredPayers = registeredPayers;
	// Filter by name if search param exists
	if (appliedParams.searchParams?.name) {
		const nameFilter = appliedParams.searchParams.name.toLowerCase();
		filteredPayers = _.filter(filteredPayers, (payer) => payer.name.toLowerCase().includes(nameFilter));
	}

	// Filter by enrollment required if filter param exists
	if (!_.isEmpty(appliedParams.filterParams?.enrollmentRequired)) {
		const enrollmentRequiredValues = appliedParams.filterParams.enrollmentRequired;

		// If both/none are selected, don't filter
		if (enrollmentRequiredValues.length !== 2) {
			filteredPayers = _.filter(filteredPayers, (payer) => {
				return _.includes(enrollmentRequiredValues, payer.enrollmentRequired);
			});
		}
	}
	// filter by status
	if (!_.isEmpty(appliedParams.filterParams?.status)) {
		const statusValues = appliedParams.filterParams.status;
		filteredPayers = _.filter(filteredPayers, (payer) => {
			const status = _.find(selectedPayers, { id: payer.id })?.status;
			if (_.isNumber(status)) {
				return _.includes(statusValues, _.toString(status) as unknown as Payers.IPayerStatus);
			} else if (_.includes(statusValues, NOT_ADDED_PAYER_FILTER.value as unknown as Payers.IPayerStatus)) {
				return true;
			}
			return false;
		});
	}
	// filter by transactions
	if (!_.isEmpty(appliedParams.filterParams?.transactions)) {
		const transactionsValues = appliedParams.filterParams.transactions;
		filteredPayers = _.filter(filteredPayers, (payer) => {
			return _.some(payer.transactions, (transaction) => _.includes(transactionsValues, transaction.transactionType));
		});
	}

	return filteredPayers;
};

export {
	DEFAULT_PROVIDER_IDENTIFIER_COMBINATION,
	NOT_ADDED_PAYER_FILTER,
	prepareSelectedPayersMeta,
	prepareAllPayersMeta,
	preparePayerMeta,
	groupTransactionMaintenance,
	getFilteredPayers,
};
